import React from 'react'

import { WoocommerceCart } from '@contracts/context/woocommerceCart'

import { getCustomerCart } from '@localApollo/queries/getCart'

import { clearStoredCart, getStoredCart, setStoredCart } from '@utils/cartStorage'

import { WoocommerceContext } from './woocommerceContext'

interface Props {
	children: React.ReactNode
}

export const WoocommerceProvider: React.FC<Props> = (props) => {
	const [cart, setCart] = React.useState<WoocommerceCart>(null)
	const [cartLoading, setCartLoading] = React.useState<boolean>(true)
	const [justAdded, setJustAdded] = React.useState<boolean>(false)

	const refreshCart = React.useCallback(async () => {
		try {
			const newCart = await getCustomerCart()
			setCart(newCart)

			if (newCart) {
				setStoredCart(newCart)
			} else {
				clearStoredCart()
			}
		} catch (error) {
			console.error('Failed to refresh cart:', error)
		} finally {
			setCartLoading(false)
		}
	}, [])

	// Initial cart setup
	React.useEffect(() => {
		const stored = getStoredCart()

		if (stored) {
			const fourHours = 4 * 60 * 60 * 1000
			if (Date.now() - stored.timestamp < fourHours) {
				setCart(stored.cart)
				setCartLoading(false)
				return
			}
		} else {
			clearStoredCart()
		}

		refreshCart()
	}, [])

	// Listen for storage events
	React.useEffect(() => {
		const handleStorageChange = (e: StorageEvent) => {
			if (e.key === 'woo-session' && !e.newValue) {
				refreshCart()
			}
		}

		window.addEventListener('storage', handleStorageChange)
		return () => window.removeEventListener('storage', handleStorageChange)
	}, [refreshCart])

	return (
		<WoocommerceContext.Provider
			value={{
				cart: [cart, setCart],
				cartLoading: [cartLoading, setCartLoading],
				justAddedToCart: [justAdded, setJustAdded],
				refreshCart,
			}}
		>
			{props.children}
		</WoocommerceContext.Provider>
	)
}
